/**
 * Agent Featured - the "Featured" attribute's backing id-set (see
 * @slowmaui/shared-types' attributes.ts). Populated by opening a
 * `?agent=<slug>` link from an agent's own landing page, not by direct
 * per-item user action.
 *
 * Deliberately simpler than agentPicks.ts, and behaves differently in two
 * ways that both trace back to the same fact - this is persistent, not a
 * one-off compiled message:
 *   - No history list. There's only ever one CURRENT featured list per
 *     agent (the backend only ever has one 'published' story at a time -
 *     see migration 048/handleStoryPublish), so there's nothing to browse
 *     back through - just "fetch fresh on `?agent=`, hold it for the
 *     session."
 *   - Not exclusive. Unlike Agent Picks (a message compiled just for one
 *     recipient, which isolates the view via CNTLDrawer.showOnlyAttribute
 *     when opened), Featured is an agent's showcase meant to sit alongside
 *     everything else on the map - it only badges/sort-boosts, same as
 *     Favorites/Alerts/New.
 */

import type { DataClient } from '@slowmaui/api-client';
import { listingItemKey } from '@slowmaui/shared-types';

const STORAGE_KEY = 'slowmaui_agent_featured';

export interface ActiveFeaturedMeta {
  agentSlug: string;
  agentName: string | null;
  itemCount: number;
}

type ChangeCallback = (ids: Set<string>, meta: ActiveFeaturedMeta | null) => void;

let itemIds: Set<string> = new Set();
let meta: ActiveFeaturedMeta | null = null;
let listeners: ChangeCallback[] = [];

function save(): void {
  try {
    if (meta) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ meta, itemIds: [...itemIds] }));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (error) {
    console.error('Failed to save agent featured to localStorage:', error);
  }
}

function notify(): void {
  listeners.forEach((cb) => cb(new Set(itemIds), meta));
}

/** Restores whatever was last active. Call once at startup, before checkUrlForAgent(). */
export function initAgentFeatured(): void {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.meta && Array.isArray(parsed.itemIds)) {
        meta = parsed.meta;
        itemIds = new Set(parsed.itemIds);
      }
    }
  } catch (error) {
    console.error('Failed to load agent featured from localStorage:', error);
    meta = null;
    itemIds = new Set();
  }
  notify();
}

export function getAgentFeatured(): Set<string> {
  return new Set(itemIds);
}

export function getActiveFeaturedMeta(): ActiveFeaturedMeta | null {
  return meta;
}

export function onAgentFeaturedChange(callback: ChangeCallback): () => void {
  listeners.push(callback);
  return () => {
    listeners = listeners.filter((l) => l !== callback);
  };
}

/**
 * Checks the current URL for `?agent=<slug>`. If present and not already
 * the active agent, fetches their published Featured list fresh. Returns
 * the newly-loaded meta (so the caller can show a toast) - null if there
 * was no `?agent=` param, that agent has nothing published (an empty
 * result doesn't overwrite whatever's already active), or it matched the
 * already-active agent (no-op, not re-toasted on a plain refresh).
 */
export async function checkUrlForAgent(api: DataClient): Promise<ActiveFeaturedMeta | null> {
  const slug = new URLSearchParams(window.location.search).get('agent');
  if (!slug || slug === meta?.agentSlug) return null;

  try {
    const result = await api.getAgentFeatured(slug);
    const ids = new Set<string>();
    result.listings.forEach((l) => ids.add(listingItemKey(l)));
    result.neighborhoods.forEach((n) => ids.add(`nh:${n.id}`));
    if (ids.size === 0) return null;

    itemIds = ids;
    meta = {
      agentSlug: slug,
      agentName: result.agentName ?? null,
      itemCount: ids.size,
    };
    save();
    notify();
    return meta;
  } catch (error) {
    console.error('Failed to load agent featured list:', error);
    return null;
  }
}
